declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;
let initPromise: Promise<boolean> | null = null;
const PROD_GA_MEASUREMENT_ID = 'G-ZDV1L1BQ0W';
const STALE_GA_MEASUREMENT_IDS = new Set(['G-DLM69Z1FJW']);
const PROD_GA_HOSTS = new Set([
  'gentle-connections.web.app',
  'gentle-connections.com',
  'www.gentle-connections.com',
]);

function readEnv(key: string) {
  return typeof import.meta !== 'undefined' ? import.meta.env[key] : '';
}

function isProductionGaHost() {
  return typeof window !== 'undefined' && PROD_GA_HOSTS.has(window.location.hostname);
}

export function resolveGoogleAnalyticsMeasurementId() {
  const measurementId = readEnv('VITE_GA_MEASUREMENT_ID');
  if (isProductionGaHost()) {
    if (!measurementId || STALE_GA_MEASUREMENT_IDS.has(measurementId)) {
      return PROD_GA_MEASUREMENT_ID;
    }
  }
  return measurementId;
}

function injectScript(measurementId: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById('gc-ga-script')) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = 'gc-ga-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Analytics'));
    document.head.appendChild(script);
  });
}

function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
}

export async function initGoogleAnalytics() {
  const measurementId = resolveGoogleAnalyticsMeasurementId();
  if (!measurementId || typeof window === 'undefined') return false;
  if (initialized) return true;
  if (initPromise) return initPromise;
  initPromise = (async () => {
    ensureGtag();
    await injectScript(measurementId);
    window.gtag?.('js', new Date());
    window.gtag?.('config', measurementId, {
      anonymize_ip: true,
      send_page_view: false,
    });
    initialized = true;
    return true;
  })().catch((error) => {
    initPromise = null;
    throw error;
  });
  return initPromise;
}

export function captureGoogleAnalyticsEvent(
  name: string,
  properties?: Record<string, unknown>,
) {
  if (!initialized) return;
  window.gtag?.('event', name, properties || {});
}

export function captureGoogleAnalyticsPageView() {
  if (!initialized) return;
  window.gtag?.('event', 'page_view');
}
